import { createAbilityChoiceBoundary } from '@cards/engine';
import { canonicalAbilityStateKey, persistedAbilityStateKey } from './state.js';
const HOOK_NAMES = Object.freeze([
    'onPlay', 'onEnterPlay', 'onLeavePlay', 'onDeath', 'onBeforeCombat',
    'onDealDamage', 'onTakeDamage', 'onAttack', 'onAttackResolved', 'onAttacked', 'onAfterCombat',
    'beforeOwnAttack', 'interceptAttack', 'beforeAlliedAttack', 'interceptIncomingDamage',
    'modifyOutgoingDamage', 'modifyIncomingDamage', 'replaceDamage', 'replaceDeath',
    'planRandomDamageReplacement', 'planReactionTrigger', 'onHackTriggered',
    'onRoundStart', 'onRoundEnd', 'ongoing', 'onAnyEvent',
]);
const MODIFIERS = new Set(['modifyOutgoingDamage', 'modifyIncomingDamage']);
const SINGLETONS = new Set([
    'replaceDeath',
    'planRandomDamageReplacement',
    'planReactionTrigger',
    'interceptAttack',
    'interceptIncomingDamage',
    // Чистый производный провайдер «Постоянно» — один на карту, passthrough.
    'ongoing',
]);
const SYNC_ONLY = new Set([
    'onHackTriggered', 'beforeOwnAttack', 'interceptAttack', 'beforeAlliedAttack', 'interceptIncomingDamage',
]);
const ABILITY_KEYS = new Set([
    'source', 'hook', 'abilityId', 'dependencies', 'state', 'meta', 'compile',
]);
const GENERATOR_FUNCTION_PROTOTYPE = Object.getPrototypeOf(function* () { return undefined; });
const ASYNC_FUNCTION_PROTOTYPE = Object.getPrototypeOf(async function () { return undefined; });
const ASYNC_GENERATOR_FUNCTION_PROTOTYPE = Object.getPrototypeOf(async function* () { return undefined; });
function asRuntimeHook(value) {
    return value;
}
function isDeclaredGenerator(handler) {
    const prototype = Object.getPrototypeOf(handler);
    return prototype === GENERATOR_FUNCTION_PROTOTYPE
        || prototype === ASYNC_GENERATOR_FUNCTION_PROTOTYPE;
}
function isDeclaredAsync(handler) {
    return Object.getPrototypeOf(handler) === ASYNC_FUNCTION_PROTOTYPE;
}
function isThenable(value) {
    return (typeof value === 'object' && value !== null) || typeof value === 'function'
        ? typeof value.then === 'function'
        : false;
}
function assertDeclaredSync(cardId, hook, handler) {
    if (isDeclaredGenerator(handler) || isDeclaredAsync(handler)) {
        throw new Error(`defineGameCard "${cardId}": ${hook} должен быть синхронным`);
    }
}
function assertAbility(cardId, ability, index) {
    if (ability === null || typeof ability !== 'object') {
        throw new Error(`defineGameCard "${cardId}": ability #${index + 1} должна быть объектом helper`);
    }
    for (const key of Reflect.ownKeys(ability)) {
        if (!ABILITY_KEYS.has(key)) {
            throw new Error(`defineGameCard "${cardId}": ability #${index + 1} содержит неизвестное поле "${String(key)}"`);
        }
    }
    if (ability.source !== 'game_card_ability' || !HOOK_NAMES.includes(ability.hook)
        || typeof ability.compile !== 'function') {
        throw new Error(`defineGameCard "${cardId}": ability #${index + 1} создана не authoring helper`);
    }
    if (ability.state !== undefined) {
        if (ability.abilityId === undefined || ability.abilityId !== ability.state.abilityId) {
            throw new Error(`defineGameCard "${cardId}": stateful ability #${index + 1} требует стабильный abilityId`);
        }
        if (!['once_per_round', 'once_per_instance'].includes(ability.state.kind)
            || !['round', 'used'].includes(ability.state.slot)) {
            throw new Error(`defineGameCard "${cardId}": stateful ability "${ability.abilityId}" имеет неизвестную state-схему`);
        }
    }
}
function assertRawHooks(cardId, rawHooks) {
    if (rawHooks === undefined)
        return;
    if (rawHooks === null || typeof rawHooks !== 'object') {
        throw new Error(`defineGameCard "${cardId}": raw hooks должен быть объектом`);
    }
    for (const key of Reflect.ownKeys(rawHooks)) {
        if (typeof key !== 'string' || !HOOK_NAMES.includes(key)) {
            throw new Error(`defineGameCard "${cardId}": неизвестный raw hook "${String(key)}"`);
        }
        if (typeof rawHooks[key] !== 'function') {
            throw new Error(`defineGameCard "${cardId}": raw hook "${key}" должен быть функцией`);
        }
    }
}
function isHookGenerator(body) {
    return typeof body === 'object' && body !== null
        && typeof body.next === 'function';
}
function composeReactive(handlers) {
    return function* (...args) {
        for (const handler of handlers) {
            const body = handler(...args);
            if (!isHookGenerator(body))
                continue;
            const iterator = body;
            const boundary = createAbilityChoiceBoundary();
            let step = iterator.next();
            while (step.done !== true) {
                const selection = yield boundary.mark(step.value);
                if (boundary.consumeFizzle()) {
                    iterator.return?.();
                    break;
                }
                step = iterator.next(selection);
            }
        }
    };
}
function assertSynchronousReturn(hook, body) {
    if (isThenable(body)) {
        throw new Error(`${hook} ability не может возвращать Promise/thenable`);
    }
    if (isHookGenerator(body)) {
        throw new Error(`${hook} ability должен быть синхронным`);
    }
}
function composeSync(hook, handlers) {
    return (...args) => {
        for (const handler of handlers) {
            const body = handler(...args);
            assertSynchronousReturn(hook, body);
        }
    };
}
function composeSynchronousSingleton(hook, handler) {
    return (...args) => {
        const result = handler(...args);
        assertSynchronousReturn(hook, result);
        return result;
    };
}
function composeModifier(handlers) {
    return (ctx, planned) => {
        let current = planned;
        for (const handler of handlers) {
            const amount = handler(ctx, current);
            if (!Number.isFinite(amount)) {
                throw new Error('modify ability должен вернуть конечное число');
            }
            current = { ...current, amount };
        }
        return current.amount;
    };
}
function composeReplaceDamage(handlers) {
    return (ctx, planned) => {
        let current = planned;
        for (const handler of handlers) {
            const next = handler(ctx, current);
            if (next === null)
                return null;
            current = next;
        }
        return current;
    };
}
function resolvedState(cardId, ability) {
    const state = ability.state;
    if (state === undefined)
        return undefined;
    const canonicalKey = canonicalAbilityStateKey(cardId, state);
    const persistedKey = persistedAbilityStateKey(cardId, state);
    return Object.freeze({
        abilityId: state.abilityId,
        kind: state.kind,
        keys: Object.freeze([Object.freeze({
                slot: state.slot,
                canonicalKey,
                persistedKey,
                ...(state.legacyKey === undefined ? {} : { legacyKey: state.legacyKey }),
            })]),
    });
}
function mergeDependencies(abilities) {
    const seen = new Set();
    const dependencies = [];
    for (const ability of abilities) {
        for (const dependency of ability.dependencies) {
            if (seen.has(dependency.id))
                continue;
            seen.add(dependency.id);
            dependencies.push(Object.freeze({ id: dependency.id }));
        }
    }
    return Object.freeze(dependencies);
}
export function compileAbilities(cardId, abilities = [], rawHooks) {
    assertRawHooks(cardId, rawHooks);
    abilities.forEach((ability, index) => assertAbility(cardId, ability, index));
    const byHook = new Map();
    const abilityIds = new Set();
    const storageKeys = new Set();
    const state = [];
    const meta = [];
    for (const ability of abilities) {
        const group = byHook.get(ability.hook) ?? [];
        group.push(ability);
        byHook.set(ability.hook, group);
        if (ability.meta !== undefined)
            meta.push(ability.meta);
        const descriptor = resolvedState(cardId, ability);
        if (descriptor === undefined)
            continue;
        if (abilityIds.has(descriptor.abilityId)) {
            throw new Error(`defineGameCard "${cardId}": повторный stateful abilityId "${descriptor.abilityId}"`);
        }
        abilityIds.add(descriptor.abilityId);
        for (const key of descriptor.keys) {
            if (storageKeys.has(key.persistedKey)) {
                throw new Error(`defineGameCard "${cardId}": повторный persisted vars key "${key.persistedKey}"`);
            }
            storageKeys.add(key.persistedKey);
        }
        state.push(descriptor);
    }
    const compiled = {};
    for (const hook of HOOK_NAMES) {
        const fragments = byHook.get(hook) ?? [];
        const raw = rawHooks?.[hook];
        if (SINGLETONS.has(hook)) {
            if (fragments.length > 1 || (fragments.length === 1 && raw !== undefined)) {
                const labels = fragments.map((fragment, index) => fragment.abilityId ?? `#${index + 1}`);
                if (raw !== undefined)
                    labels.push('raw');
                throw new Error(`defineGameCard "${cardId}": singleton hook "${hook}" конфликтует (${labels.join(', ')})`);
            }
            const only = fragments[0];
            if (only !== undefined) {
                const handler = only.compile(cardId);
                if (typeof handler !== 'function') {
                    throw new Error(`defineGameCard "${cardId}": ability для "${hook}" не скомпилировалась в функцию`);
                }
                const runtime = asRuntimeHook(handler);
                if (SYNC_ONLY.has(hook)) {
                    assertDeclaredSync(cardId, hook, runtime);
                    compiled[hook] = composeSynchronousSingleton(hook, runtime);
                }
                else {
                    compiled[hook] = runtime;
                }
            }
            else if (raw !== undefined) {
                const runtime = asRuntimeHook(raw);
                if (SYNC_ONLY.has(hook)) {
                    assertDeclaredSync(cardId, hook, runtime);
                    compiled[hook] = composeSynchronousSingleton(hook, runtime);
                }
                else {
                    compiled[hook] = runtime;
                }
            }
            continue;
        }
        // A raw-only hook stays byte-for-byte the author's function. Composition
        // is introduced only when ability fragments actually participate.
        if (fragments.length === 0 && raw !== undefined && !SYNC_ONLY.has(hook)) {
            compiled[hook] = asRuntimeHook(raw);
            continue;
        }
        const handlers = fragments.map((fragment) => {
            const handler = fragment.compile(cardId);
            if (typeof handler !== 'function') {
                throw new Error(`defineGameCard "${cardId}": ability для "${hook}" не скомпилировалась в функцию`);
            }
            return asRuntimeHook(handler);
        });
        if (raw !== undefined)
            handlers.push(asRuntimeHook(raw));
        if (handlers.length === 0)
            continue;
        if (MODIFIERS.has(hook))
            compiled[hook] = composeModifier(handlers);
        else if (hook === 'replaceDamage')
            compiled[hook] = composeReplaceDamage(handlers);
        else if (SYNC_ONLY.has(hook)) {
            if (handlers.some((handler) => isDeclaredGenerator(handler) || isDeclaredAsync(handler))) {
                throw new Error(`defineGameCard "${cardId}": ${hook} ability должен быть синхронным`);
            }
            compiled[hook] = composeSync(hook, handlers);
        }
        else
            compiled[hook] = composeReactive(handlers);
    }
    const hookKeys = Object.keys(compiled);
    return Object.freeze({
        ...(hookKeys.length === 0 ? {} : { hooks: compiled }),
        dependencies: mergeDependencies(abilities),
        state: Object.freeze(state),
        meta: Object.freeze(meta),
    });
}
//# sourceMappingURL=compose.js.map